import { useEffect, useRef, useState } from "react";
import {
  HomeHeroesDataType,
  HomeHeroFeatureDataType,
  languagesDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm } from "antd";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import { FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import { HomeHeroesService } from "../../services/home_heroes.service";
import { HomeHeroFeaturesService } from "../../services/home_hero_features.service";
import SelectImage from "../../components/shared/select-image/SelectImage";
import ShowImage from "../../components/shared/show-image/ShowImage";

// ─── Tip köməkçisi ───────────────────────────────────────────────────────────
type FeatureLangField = "title" | "description";

const HomeHeroes = () => {
  // ── Ana Hero state ──────────────────────────────────────────────────────────
  const [hero, setHero] = useState<HomeHeroesDataType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isHeroModalVisible, setIsHeroModalVisible] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [langCode, setLangCode] = useState("AZ");
  const [heroFileList, setHeroFileList] = useState<any[]>([]);
  const heroFileListRef = useRef<any[]>([]);
  const setHeroFileListSynced = (list: any[]) => {
    heroFileListRef.current = list;
    setHeroFileList(list);
  };
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");

  // ── Feature state ───────────────────────────────────────────────────────────
  const [features, setFeatures] = useState<HomeHeroFeatureDataType[]>([]);
  const [featuresLoading, setFeaturesLoading] = useState<boolean>(true);
  const [isFeatureModalVisible, setIsFeatureModalVisible] = useState<boolean>(false);
  const [isFeatureCreating, setIsFeatureCreating] = useState<boolean>(true);
  const [editingFeatureId, setEditingFeatureId] = useState<number | null>(null);
  const [featureLangCode, setFeatureLangCode] = useState("AZ");
  const [featureFileList, setFeatureFileList] = useState<any[]>([]);
  const featureFileListRef = useRef<any[]>([]);
  const setFeatureFileListSynced = (list: any[]) => {
    featureFileListRef.current = list;
    setFeatureFileList(list);
  };

  // ─── Data çəkmə ────────────────────────────────────────────────────────────
  const fetchHero = () => {
    setLoading(true);
    HomeHeroesService.getHomeHeroes()
      .then((response) => {
        const data = response?.data?.data ?? response?.data ?? null;
        setHero(Array.isArray(data) ? (data.length > 0 ? data[0] : null) : data);
      })
      .finally(() => setLoading(false));
  };

  const fetchFeatures = () => {
    setFeaturesLoading(true);
    HomeHeroFeaturesService.getAllFeatures()
      .then((response) => {
        const data = response?.data?.data ?? response?.data ?? [];
        setFeatures(Array.isArray(data) ? data : []);
      })
      .finally(() => setFeaturesLoading(false));
  };

  useEffect(() => {
    fetchHero();
    fetchFeatures();
  }, []);

  useEffect(() => {
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data)
    );
  }, []);

  // ─── Ana Hero Formik ────────────────────────────────────────────────────────
  const {
    handleSubmit: heroSubmit,
    values: heroValues,
    resetForm: heroReset,
    setFieldValue: heroSetField,
    touched: heroTouched,
    errors: heroErrors,
  } = useFormik({
    initialValues: {
      image: null as any,
      translations: [] as {
        locale: string;
        title_1: string;
        title_2: string;
        description: string;
      }[],
      title_1: "",
      title_2: "",
      description: "",
    },
    onSubmit: async (vals) => {
      const azTranslation = {
        locale: "az",
        title_1: vals.title_1,
        title_2: vals.title_2,
        description: vals.description,
      };
      const updatedTranslations = [
        azTranslation,
        ...vals.translations.filter((t) => t.locale !== "az"),
      ];
      const formData = new FormData();
      const heroImageFile = heroFileListRef.current[0]?.originFileObj ?? (vals.image && typeof vals.image !== "string" ? vals.image : null);
      if (heroImageFile) {
        formData.append("image", heroImageFile);
      }
      updatedTranslations.forEach((t, i) => {
        formData.append(`translations[${i}][locale]`, t.locale);
        formData.append(`translations[${i}][title_1]`, t.title_1);
        formData.append(`translations[${i}][title_2]`, t.title_2);
        formData.append(`translations[${i}][description]`, t.description);
      });

      if (isCreating || !hero) {
        const response = await HomeHeroesService.createHomeHeroes(formData);
        if (response.status === 200 || response.status === 201) {
          message.success("Ana Hero uğurla əlavə edildi!");
          setIsHeroModalVisible(false);
          heroReset();
          setHeroFileList([]);
          fetchHero();
        } else {
          message.error(response.errorMessage || "Əlavə etmək alınmadı.");
        }
      } else {
        const response = await HomeHeroesService.updateHomeHeroes(hero.id, formData);
        if (response.status === 200 || response.status === 201) {
          message.success("Ana Hero uğurla yeniləndi!");
          setIsHeroModalVisible(false);
          heroReset();
          setHeroFileList([]);
          fetchHero();
        } else {
          message.error(response.errorMessage || "Yeniləmək alınmadı.");
        }
      }
    },
  });

  // ── Hero tərcümə köməkçiləri ────────────────────────────────────────────────
  const handleHeroTranslationChange = (
    value: string,
    field: "title_1" | "title_2" | "description"
  ) => {
    if (langCode === "AZ") {
      heroSetField(field, value);
    } else {
      const locale = langCode.toLowerCase();
      const updated = [...heroValues.translations];
      const idx = updated.findIndex((t) => t.locale === locale);
      if (idx !== -1) {
        updated[idx] = { ...updated[idx], [field]: value };
      } else {
        updated.push({
          locale,
          title_1: field === "title_1" ? value : "",
          title_2: field === "title_2" ? value : "",
          description: field === "description" ? value : "",
        });
      }
      heroSetField("translations", updated);
    }
  };

  const getHeroTranslationValue = (field: "title_1" | "title_2" | "description") => {
    if (langCode === "AZ") return heroValues[field];
    const locale = langCode.toLowerCase();
    return heroValues.translations.find((t) => t.locale === locale)?.[field] ?? "";
  };

  const openCreateModal = () => {
    setIsCreating(true);
    heroReset();
    setHeroFileList([]);
    setLangCode("AZ");
    setIsHeroModalVisible(true);
  };

  const openEditModal = () => {
    if (!hero) return;
    setIsCreating(false);
    const azTrans = hero.translations.find((t) => t.locale === "az" || t.locale === "AZ");
    const otherTrans = hero.translations.filter((t) => t.locale !== "az" && t.locale !== "AZ");
    heroSetField("title_1", azTrans?.title_1 ?? "");
    heroSetField("title_2", azTrans?.title_2 ?? "");
    heroSetField("description", azTrans?.description ?? "");
    heroSetField("translations", otherTrans);
    heroSetField("image", hero.image ?? null);
    setHeroFileList(
      hero.image
        ? [{ uid: "-1", name: "hero image", status: "done", url: hero.image }]
        : []
    );
    setLangCode("AZ");
    setIsHeroModalVisible(true);
  };

  const handleHeroDelete = async () => {
    if (!hero) return;
    const response = await HomeHeroesService.deleteHomeHeroes(hero.id);
    if (response.status === 200 || response.status === 204) {
      message.success("Ana Hero silindi!");
      setHero(null);
    } else {
      message.error(response.errorMessage || "Silmək alınmadı.");
    }
  };

  // ─── Feature Formik ─────────────────────────────────────────────────────────
  const {
    handleSubmit: featureSubmit,
    values: featureValues,
    resetForm: featureReset,
    setFieldValue: featureSetField,
    touched: featureTouched,
    errors: featureErrors,
  } = useFormik({
    initialValues: {
      image: null as any,
      translations: [] as { locale: string; title: string; description: string }[],
      title: "",
      description: "",
    },
    onSubmit: async (vals) => {
      const azTranslation = {
        locale: "az",
        title: vals.title,
        description: vals.description,
      };
      const updatedTranslations = [
        azTranslation,
        ...vals.translations.filter((t) => t.locale !== "az"),
      ];
      const formData = new FormData();
      // featureFileListRef-dən həqiqi File obyektini götürürük (stale closure yoxdur)
      const imageFile = featureFileListRef.current[0]?.originFileObj ?? (vals.image && typeof vals.image !== "string" ? vals.image : null);
      if (imageFile) {
        formData.append("image", imageFile);
      }
      updatedTranslations.forEach((t, i) => {
        formData.append(`translations[${i}][locale]`, t.locale);
        formData.append(`translations[${i}][title]`, t.title);
        formData.append(`translations[${i}][description]`, t.description);
      });

      if (isFeatureCreating || !editingFeatureId) {
        const response = await HomeHeroFeaturesService.createFeature(formData);
        if (response.status === 200 || response.status === 201) {
          message.success("Xüsusiyyət uğurla əlavə edildi!");
          setIsFeatureModalVisible(false);
          featureReset();
          setFeatureFileList([]);
          fetchFeatures();
        } else {
          message.error(response.errorMessage || "Əlavə etmək alınmadı.");
        }
      } else {
        const response = await HomeHeroFeaturesService.updateFeature(editingFeatureId, formData);
        if (response.status === 200 || response.status === 201) {
          message.success("Xüsusiyyət uğurla yeniləndi!");
          setIsFeatureModalVisible(false);
          featureReset();
          setFeatureFileList([]);
          fetchFeatures();
        } else {
          message.error(response.errorMessage || "Yeniləmək alınmadı.");
        }
      }
    },
  });

  // ── Feature tərcümə köməkçiləri ─────────────────────────────────────────────
  const handleFeatureTranslationChange = (
    value: string,
    field: FeatureLangField
  ) => {
    if (featureLangCode === "AZ") {
      featureSetField(field, value);
    } else {
      const locale = featureLangCode.toLowerCase();
      const updated = [...featureValues.translations];
      const idx = updated.findIndex((t) => t.locale === locale);
      if (idx !== -1) {
        updated[idx] = { ...updated[idx], [field]: value };
      } else {
        updated.push({
          locale,
          title: field === "title" ? value : "",
          description: field === "description" ? value : "",
        });
      }
      featureSetField("translations", updated);
    }
  };

  const getFeatureTranslationValue = (field: FeatureLangField) => {
    if (featureLangCode === "AZ") return featureValues[field];
    const locale = featureLangCode.toLowerCase();
    return featureValues.translations.find((t) => t.locale === locale)?.[field] ?? "";
  };

  const openFeatureModal = () => {
    setIsFeatureCreating(true);
    setEditingFeatureId(null);
    featureReset();
    setFeatureFileList([]);
    setFeatureLangCode("AZ");
    setIsFeatureModalVisible(true);
  };

  const openFeatureEditModal = (feature: HomeHeroFeatureDataType) => {
    setIsFeatureCreating(false);
    setEditingFeatureId(feature.id);
    const azTrans = feature.translations.find((t) => t.locale === "az" || t.locale === "AZ");
    const otherTrans = feature.translations.filter((t) => t.locale !== "az" && t.locale !== "AZ");
    featureSetField("title", azTrans?.title ?? "");
    featureSetField("description", azTrans?.description ?? "");
    featureSetField("translations", otherTrans);
    featureSetField("image", feature.image ?? null);
    setFeatureFileList(
      feature.image
        ? [{ uid: "-1", name: "feature image", status: "done", url: feature.image }]
        : []
    );
    setFeatureLangCode("AZ");
    setIsFeatureModalVisible(true);
  };

  const handleFeatureDelete = async (id: number) => {
    const response = await HomeHeroFeaturesService.deleteFeature(id);
    if (response.status === 200 || response.status === 204) {
      message.success("Xüsusiyyət silindi!");
      setFeatures((prev) => prev.filter((f) => f.id !== id));
    } else {
      message.error(response.errorMessage || "Silmək alınmadı.");
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div id="pages">
      <Helmet>
        <title>1412 | Ana Hero</title>
      </Helmet>

      {loading ? (
        <Spinner />
      ) : (
        <div>
          {/* ── ANA HERO ──────────────────────────────────────── */}
          <div className="page_head">
            <h3 className="page_title">Ana Hero</h3>
          </div>

          <div className="pages_components">
            {!hero ? (
              <MainButton onClick={openCreateModal}>
                <FaPlus size={20} />
                &nbsp; Yenisini yarat
              </MainButton>
            ) : (
              <div style={{ display: "flex", gap: "12px" }}>
                <MainButton onClick={openEditModal}>
                  <FaEdit size={20} />
                </MainButton>
                <Popconfirm
                  title="Ana Hero-nu silmək istəyirsiniz?"
                  onConfirm={handleHeroDelete}
                  onCancel={() => message.info("Silmə ləğv edildi.")}
                  okText="Bəli"
                  cancelText="Xeyr"
                  placement="bottomLeft"
                >
                  <MainButton>
                    <FaTrash size={20} />
                  </MainButton>
                </Popconfirm>
              </div>
            )}

            {hero && (
              <div className="pages_table" style={{ marginTop: "24px" }}>
                {hero.image && (
                  <div style={{ marginBottom: "20px" }}>
                    <img
                      src={'https://1412api.bionet.az/storage/'+hero.image}
                      alt="hero"
                      style={{
                        width: "100%",
                        maxWidth: "600px",
                        height: "340px",
                        objectFit: "cover",
                        borderRadius: "12px",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setSelectedImage(hero.image);
                        setShowImageModal(true);
                      }}
                    />
                  </div>
                )}

                <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "12px" }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Dil</th>
                      <th style={thStyle}>Başlıq 1</th>
                      <th style={thStyle}>Başlıq 2</th>
                      <th style={thStyle}>Açıqlama</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hero.translations.map((t, i) => (
                      <tr key={i}>
                        <td style={tdStyle}>{t.locale?.toUpperCase()}</td>
                        <td style={tdStyle}>{t.title_1}</td>
                        <td style={tdStyle}>{t.title_2}</td>
                        <td style={tdStyle}>{t.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── HERO XÜSUSİYYƏTLƏRİ ──────────────────────────── */}
          <div className="page_head" style={{ marginTop: "40px" }}>
            <h3 className="page_title">Hero Xüsusiyyətləri</h3>
          </div>

          <div className="pages_components">
            <MainButton onClick={openFeatureModal}>
              <FaPlus size={20} />
              &nbsp; Xüsusiyyət əlavə et
            </MainButton>

            {featuresLoading ? (
              <Spinner />
            ) : (
              <div className="pages_table" style={{ marginTop: "24px" }}>
                {features.length === 0 ? (
                  <p style={{ color: "#999", marginTop: "12px" }}>
                    Hələ xüsusiyyət əlavə edilməyib.
                  </p>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>№</th>
                        <th style={thStyle}>Şəkil</th>
                        <th style={thStyle}>Dil</th>
                        <th style={thStyle}>Başlıq</th>
                        {/* <th style={thStyle}>Açıqlama</th> */}
                        <th style={thStyle}>
                          <div className="settings">
                            <i className="fa-solid fa-gear"></i>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {features.map((feature, idx) =>
                        feature.translations.map((t, ti) => (
                          <tr key={`${feature.id}-${ti}`}>
                            {ti === 0 && (
                              <>
                                <td
                                  style={tdStyle}
                                  rowSpan={feature.translations.length}
                                >
                                  {idx + 1}
                                </td>
                                <td
                                  style={tdStyle}
                                  rowSpan={feature.translations.length}
                                >
                                  {feature.image ? (
                                    <img
                                      src={ 'https://1412api.bionet.az/storage/'+feature.image}
                                      alt="feature"
                                      style={{
                                        width: "48px",
                                        height: "48px",
                                        objectFit: "cover",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                      }}
                                      onClick={() => {
                                        setSelectedImage(feature.image);
                                        setShowImageModal(true);
                                      }}
                                    />
                                  ) : (
                                    "—"
                                  )}
                                </td>
                              </>
                            )}
                            <td style={tdStyle}>{t.locale?.toUpperCase()}</td>
                            <td style={tdStyle}>{t.title}</td>
                            {/* <td style={tdStyle}>{t.description}</td> */}
                            {ti === 0 && (
                              <td
                                style={tdStyle}
                                rowSpan={feature.translations.length}
                              >
                                <div className="settings_icons">
                                  <div className="edit" onClick={() => openFeatureEditModal(feature)}>
                                    <i className="fa-solid fa-pen-to-square"></i>
                                  </div>
                                  <Popconfirm
                                    title="Bu xüsusiyyəti silmək istəyirsiniz?"
                                    onConfirm={() => handleFeatureDelete(feature.id)}
                                    onCancel={() =>
                                      message.info("Silmə ləğv edildi.")
                                    }
                                    okText="Bəli"
                                    cancelText="Xeyr"
                                    placement="topRight"
                                  >
                                    <div className="delete">
                                      <i className="fa-solid fa-trash"></i>
                                    </div>
                                  </Popconfirm>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── ANA HERO MODALI ──────────────────────────────────────────────────── */}
      <Modal
        title={isCreating ? "Yeni Ana Hero əlavə edin" : "Ana Hero-nu redaktə edin"}
        open={isHeroModalVisible}
        onCancel={() => {
          setIsHeroModalVisible(false);
          heroReset();
          setHeroFileList([]);
        }}
        footer={null}
        style={{ minWidth: "340px" }}
        width={"100%"}
        centered
      >
        <form className="modal_form" onSubmit={heroSubmit}>
          <div className="langs">
            {langs?.map((lang, index) => (
              <div key={index}>
                <button
                  className={lang.code === langCode ? "active_lang_btn" : ""}
                  type="button"
                  onClick={() => setLangCode(lang.code)}
                >
                  {lang.name}
                </button>
              </div>
            ))}
          </div>

          <div className="form_input">
            <label className="label" htmlFor="hero_title_1">
              Başlıq 1 ({langCode})
            </label>
            <div className="input_wrapper">
              <input
                id="hero_title_1"
                placeholder="Başlıq 1"
                value={getHeroTranslationValue("title_1")}
                onChange={(e) => handleHeroTranslationChange(e.target.value, "title_1")}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={heroErrors.title_1} touched={heroTouched.title_1} />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="hero_title_2">
              Başlıq 2 ({langCode})
            </label>
            <div className="input_wrapper">
              <input
                id="hero_title_2"
                placeholder="Başlıq 2"
                value={getHeroTranslationValue("title_2")}
                onChange={(e) => handleHeroTranslationChange(e.target.value, "title_2")}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={heroErrors.title_2} touched={heroTouched.title_2} />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="hero_description">
              Açıqlama ({langCode})
            </label>
            <div className="input_wrapper">
              <input
                id="hero_description"
                placeholder="Açıqlama"
                value={getHeroTranslationValue("description")}
                onChange={(e) => handleHeroTranslationChange(e.target.value, "description")}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={heroErrors.description} touched={heroTouched.description} />
          </div>

          <div className="form_input">
            <SelectImage
              image_name="Şəkil seçin"
              logoFileList={heroFileList}
              setLogoFileList={setHeroFileListSynced}
              field="image"
              setFieldValue={heroSetField}
            />
            <ErrorMessage
              error={heroErrors.image as string | undefined}
              touched={heroTouched.image as boolean | undefined}
            />
          </div>

          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isCreating ? "Əlavə edin" : "Redaktə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>

      {/* ── FEATURE MODALI ───────────────────────────────────────────────────── */}
      <Modal
        title={isFeatureCreating ? "Yeni Xüsusiyyət əlavə edin" : "Xüsusiyyəti redaktə edin"}
        open={isFeatureModalVisible}
        onCancel={() => {
          setIsFeatureModalVisible(false);
          featureReset();
          setFeatureFileList([]);
        }}
        footer={null}
        style={{ minWidth: "340px" }}
        width={"100%"}
        centered
      >
        <form className="modal_form" onSubmit={featureSubmit}>
          <div className="langs">
            {langs?.map((lang, index) => (
              <div key={index}>
                <button
                  className={lang.code === featureLangCode ? "active_lang_btn" : ""}
                  type="button"
                  onClick={() => setFeatureLangCode(lang.code)}
                >
                  {lang.name}
                </button>
              </div>
            ))}
          </div>

          <div className="form_input">
            <label className="label" htmlFor="feature_title">
              Başlıq ({featureLangCode})
            </label>
            <div className="input_wrapper">
              <input
                id="feature_title"
                placeholder="Başlıq"
                value={getFeatureTranslationValue("title")}
                onChange={(e) => handleFeatureTranslationChange(e.target.value, "title")}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={featureErrors.title} touched={featureTouched.title} />
          </div>

          {/* <div className="form_input">
            <label className="label" htmlFor="feature_description">
              Açıqlama ({featureLangCode})
            </label>
            <div className="input_wrapper">
              <input
                id="feature_description"
                placeholder="Açıqlama"
                value={getFeatureTranslationValue("description")}
                onChange={(e) =>
                  handleFeatureTranslationChange(e.target.value, "description")
                }
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage
              error={featureErrors.description}
              touched={featureTouched.description}
            />
          </div> */}

          <div className="form_input">
            <SelectImage
              image_name="Şəkil seçin"
              logoFileList={featureFileList}
              setLogoFileList={setFeatureFileListSynced}
              field="image"
              setFieldValue={featureSetField}
            />
            <ErrorMessage
              error={featureErrors.image as string | undefined}
              touched={featureTouched.image as boolean | undefined}
            />
          </div>

          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isFeatureCreating ? "Əlavə edin" : "Redaktə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>

      <ShowImage
        showImageModal={showImageModal}
        setShowImagesModal={setShowImageModal}
        image={selectedImage}
      />
    </div>
  );
};

// ── Cədvəl stilləri ──────────────────────────────────────────────────────────
const thStyle: React.CSSProperties = {
  border: "1px solid #e0e0e0",
  padding: "10px 14px",
  textAlign: "left",
  background: "#f5f5f5",
  fontWeight: 600,
};

const tdStyle: React.CSSProperties = {
  border: "1px solid #e0e0e0",
  padding: "10px 14px",
  verticalAlign: "middle",
};

export default HomeHeroes;
